// Scheduling (legacy api `modules/Scheduling`; field notes backoffice.employees.schedule and register.dialog.my-schedule):
// a location's week of planned shifts as config (ADR-015). A schedule (`labor.schedule`) is one week, a draft until it is
// published; a shift (`labor.shift`) belongs to one schedule, dated by its business day with wall times on the location
// clock and its planned breaks snapshotted from the break definitions (legacy `shift_breaks`). Core's `labor/schedule`
// holds the rules: a shift's span, paid minutes and cost, the week start, what may be written and the register's My
// Shifts figures. Copy Week is a schedule then its shifts, written by the client one command each.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { BreakPolicyId, EmployeeId, EpochMs, JobRoleId, LocalDate, LocationId, Money, ScheduleId, ScheduleTemplateId, ShiftId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten } from '../config/index.ts';
import { EmployeeNotFound, LocationTime } from './index.ts';

const WallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).meta({ id: 'ShiftTime', description: '"HH:MM" on the location clock.' });

export const SchedulePublication = z.enum(['draft', 'published']).meta({ id: 'SchedulePublication' });

export const ScheduleView = z
  .object({
    scheduleId: ScheduleId,
    locationId: LocationId,
    /** The first day of the week (the location's week start day). */
    weekStartsOn: LocalDate,
    publication: SchedulePublication,
    /** When it was last published, on the location clock ("Last published October 11, 2026 at 12:24 pm"). */
    publishedAt: LocationTime.nullable(),
    notes: z.string().max(1000).nullable(),
    version: z.int().nonnegative(),
  })
  .meta({ id: 'ScheduleView' });

/** A planned break as the shift dialog adds it: a break definition, how many times. */
export const PlannedBreakInput = z.object({ breakPolicyId: BreakPolicyId, quantity: z.int().min(1).max(10) }).meta({ id: 'PlannedBreakInput' });

/** A planned break as stored: the definition's name, minutes and paid flag when it was planned (legacy snapshot). */
export const PlannedBreak = z
  .object({ breakPolicyId: BreakPolicyId, name: z.string(), minutes: z.int().nonnegative(), paid: z.boolean(), quantity: z.int().min(1).max(10) })
  .meta({ id: 'PlannedBreak' });

export const ShiftChanges = z
  .object({
    /** Null: an open shift anyone may claim (legacy `is_open`). */
    employeeId: EmployeeId.nullable(),
    jobRoleId: JobRoleId.nullable(),
    businessDate: LocalDate,
    startTime: WallTime,
    /** Not after the start: the shift runs past midnight. */
    endTime: WallTime,
    breaks: z.array(PlannedBreakInput).max(10),
    notes: z.string().max(1000).nullable(),
    color: z.string().regex(/^#[0-9A-Fa-f]{6}$/).nullable(),
  })
  .partial()
  .meta({ id: 'ShiftChanges' });

export const ShiftView = z
  .object({
    shiftId: ShiftId,
    scheduleId: ScheduleId,
    locationId: LocationId,
    employeeId: EmployeeId.nullable(),
    jobRoleId: JobRoleId.nullable(),
    businessDate: LocalDate,
    startTime: WallTime,
    endTime: WallTime,
    breaks: z.array(PlannedBreak),
    notes: z.string().nullable(),
    color: z.string().nullable(),
    /** When it starts and ends (core `shiftSpan`: a start before the business-day start is the next calendar day). */
    startsAt: LocationTime,
    endsAt: LocationTime,
    /** Clock span, the gross hours My Schedule shows. */
    durationMinutes: z.int().nonnegative(),
    /** The span less the unpaid planned breaks: the paid hours the grid shows and costs. */
    paidMinutes: z.int().nonnegative(),
    /**
     * When its conflict was marked resolved (right-click › Resolve Conflict; legacy `conflict_override_at`), on the
     * location clock; null when it is not. The schedule then stops reporting this shift's conflicts, until its employee,
     * date or times change. Null on an answer recorded before the field existed.
     */
    conflictResolvedAt: LocationTime.nullable().default(null),
    version: z.int().nonnegative(),
  })
  .meta({ id: 'ShiftView' });

export const ScheduleNotFound = defineError({
  code: 'labor.schedule_not_found', httpStatus: 404, message: 'errors.labor.schedule_not_found', retryable: false,
  details: z.object({ scheduleId: ScheduleId }),
});
export const ShiftNotFound = defineError({
  code: 'labor.shift_not_found', httpStatus: 404, message: 'errors.labor.shift_not_found', retryable: false,
  details: z.object({ shiftId: ShiftId }),
});
export const ScheduleExists = defineError({
  code: 'labor.schedule_exists', httpStatus: 409, message: 'errors.labor.schedule_exists', retryable: false,
  details: z.object({ weekStartsOn: LocalDate }),
});
/** Core `ScheduleRefusal`: the week start, the shift's week, its breaks, publishing and deleting. */
export const ScheduleRefused = defineError({
  code: 'labor.schedule_refused', httpStatus: 422, message: 'errors.labor.schedule_refused', retryable: false,
  details: z.object({ rule: z.enum(['not_week_start', 'outside_week', 'too_many_breaks', 'breaks_exceed_shift', 'not_draft', 'no_shifts', 'published', 'break_retired', 'employee_inactive', 'employee_not_on_staff']) }),
});

const scheduleAdmin = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'labor.timecards.manage', risk: 'low', dryRun: true,
  agent: { expose: false },
} as const;

export const CreateSchedule = defineCommand({
  ...scheduleAdmin,
  name: 'labor.createSchedule',
  summary: 'Create the draft schedule of a week (Create Schedule, and the first step of Copy Week).',
  input: z.object({ scheduleId: ScheduleId, locationId: LocationId, weekStartsOn: LocalDate, notes: z.string().max(1000).nullable().optional() }),
  output: ScheduleView,
  errors: [ScheduleExists, ScheduleRefused, CloudAuthoritative],
  legacy: ['POST /v1/schedules', 'POST /v1/schedules/{id}/copy'],
});

export const PublishSchedule = defineCommand({
  ...scheduleAdmin,
  name: 'labor.publishSchedule',
  summary: "Publish a draft week's schedule: its shifts show on the register's My Schedule.",
  input: z.object({ scheduleId: ScheduleId, locationId: LocationId }),
  output: ScheduleView,
  errors: [ScheduleNotFound, ScheduleRefused, CloudAuthoritative],
  legacy: ['POST /v1/schedules/{id}/publish'],
});

export const DeleteSchedule = defineCommand({
  ...scheduleAdmin,
  name: 'labor.deleteSchedule',
  summary: 'Delete a draft schedule and its shifts; a published one is kept.',
  input: z.object({ scheduleId: ScheduleId, locationId: LocationId }),
  output: ScheduleView,
  errors: [ScheduleNotFound, ScheduleRefused, CloudAuthoritative],
  legacy: ['DELETE /v1/schedules/{id}'],
});

export const UpsertShift = defineCommand({
  ...scheduleAdmin,
  name: 'labor.upsertShift',
  summary: "Add a shift to a week's schedule (Add Shift, Duplicate) or change one; a new shift needs a date, start and end.",
  input: z.object({ shiftId: ShiftId, locationId: LocationId, scheduleId: ScheduleId, changes: ShiftChanges }),
  output: ShiftView,
  errors: [ScheduleNotFound, ShiftNotFound, ScheduleRefused, CloudAuthoritative],
  legacy: ['POST /v1/shifts', 'PUT /v1/shifts/{id}'],
});

export const DeleteShift = defineCommand({
  ...scheduleAdmin,
  name: 'labor.deleteShift',
  summary: 'Delete a shift.',
  input: z.object({ shiftId: ShiftId, locationId: LocationId }),
  output: ShiftView,
  errors: [ShiftNotFound, CloudAuthoritative],
  legacy: ['DELETE /v1/shifts/{id}'],
});

/**
 * Schedule › right-click a shift › Resolve Conflict / Unresolve Conflict (legacy `POST` and `DELETE
 * /v1/shifts/{id}/resolve-conflict`): mark the shift's conflicts as seen and accepted, or take the mark back.
 */
export const ResolveShiftConflict = defineCommand({
  ...scheduleAdmin,
  name: 'labor.resolveShiftConflict',
  summary: "Mark a shift's schedule conflicts resolved (they stop being reported), or unmark them.",
  input: z.object({ shiftId: ShiftId, locationId: LocationId, resolved: z.boolean() }),
  output: ShiftView,
  errors: [ShiftNotFound, CloudAuthoritative],
  legacy: ['POST /v1/shifts/{id}/resolve-conflict', 'DELETE /v1/shifts/{id}/resolve-conflict'],
});

// ---- open shifts -------------------------------------------------------------------------------------------------

/**
 * Core `ClaimRefusal`: claims off at the location, the shift taken or started, an overlap with the claimant's own,
 * approved time off that day (`date`, the shift's business date; legacy a29bc0426), a request already pending, a review
 * of nothing pending or by someone who may not approve; `not_found` a request no longer on the shift (Remove).
 * `time_off` and `not_found` were added after registers shipped: an older register words them as its generic refusal.
 */
export const ShiftClaimRefused = defineError({
  code: 'labor.shift_claim_refused', httpStatus: 422, message: 'errors.labor.shift_claim_refused', retryable: false,
  details: z.object({
    rule: z.enum(['claims_disabled', 'not_open', 'started', 'overlaps', 'time_off', 'already_requested', 'not_pending', 'not_approver', 'not_found']),
    date: LocalDate.optional(),
  }),
});

const ClaimOutput = z.object({ outcome: z.enum(['claimed', 'requested', 'approved', 'denied', 'closed']), shift: ShiftView }).meta({ id: 'ShiftClaimOutcome' });

/**
 * My Schedule › Open Shifts › Claim / Request (legacy `POST /v1/employee-open-shifts/{id}/claim`): the employee the
 * register's Time Clock identified by PIN takes an open shift, or, when the location requires approval and they may not
 * approve claims, requests it. Decided at the location (the shift is location config).
 */
export const ClaimOpenShift = defineCommand({
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: { session: 'device' }, risk: 'low', dryRun: false, agent: { expose: false },
  name: 'labor.claimOpenShift',
  summary: 'Claim an open shift for an employee, or request it when the location requires a manager to approve claims.',
  input: z.object({ shiftId: ShiftId, locationId: LocationId, employeeId: EmployeeId, claimId: z.uuid() }),
  output: ClaimOutput,
  // EmployeeNotFound: the employee is not on the location's active staff (SEC-34).
  errors: [ShiftNotFound, ShiftClaimRefused, EmployeeNotFound, CloudAuthoritative],
  legacy: ['POST /v1/employee-open-shifts/{id}/claim'],
});

/** Approve Request › Approve / Deny (legacy `PUT /v1/employee-shift-claims/{id}/review`): an approver reviews a pending request. */
export const ReviewShiftClaim = defineCommand({
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: { session: 'device' }, risk: 'low', dryRun: false, agent: { expose: false },
  name: 'labor.reviewShiftClaim',
  summary: "Approve or deny an employee's request for an open shift; approving gives them the shift.",
  input: z.object({
    shiftId: ShiftId, locationId: LocationId, claimId: z.uuid(), reviewerEmployeeId: EmployeeId, decision: z.enum(['approved', 'denied']),
    notes: z.string().trim().max(1000).nullable().optional(),
  }),
  output: ClaimOutput,
  // EmployeeNotFound: the reviewer is not on the location's active staff (B581 review).
  errors: [ShiftNotFound, ShiftClaimRefused, EmployeeNotFound, CloudAuthoritative],
  legacy: ['PUT /v1/employee-shift-claims/{id}/review'],
});

/** One party's week before and after approving, against the weekly hours limit (legacy ShiftSwapImpact; advisory); swaps and claims both read it. */
export const ShiftSwapPartyImpact = z
  .object({ hoursBefore: z.number(), hoursAfter: z.number(), maxHoursWeek: z.number(), overCap: z.boolean() })
  .meta({ id: 'ShiftSwapPartyImpact' });

// ---- Employees › Shift Claims (SCR-082; legacy ShiftClaimRequests.vue, ShiftClaimController) ------------------------

/** `filled`: the shift went to someone else; `closed`: out of the queue with no ruling (legacy `ClaimStatus::Closed`). */
export const ShiftClaimStatus = z.enum(['pending', 'approved', 'denied', 'filled', 'closed']).meta({ id: 'ShiftClaimStatus' });

/** An open-shift claim request as the Back Office lists it: the requester, the shift, its status and who reviewed it. */
export const ShiftClaimView = z
  .object({
    claimId: z.uuid(),
    shiftId: ShiftId,
    employeeId: EmployeeId,
    employeeName: z.string().nullable(),
    shift: z.object({ businessDate: LocalDate, startTime: WallTime, endTime: WallTime, jobRoleName: z.string().nullable() }),
    status: ShiftClaimStatus,
    requestedAt: LocationTime,
    /** A register employee (`staff`) or a Back Office manager (`user`); null until reviewed (a filled request names nobody). */
    reviewedBy: z.object({ kind: z.enum(['staff', 'user']), id: z.string(), name: z.string().nullable() }).nullable(),
    reviewedAt: LocationTime.nullable(),
    /** The reviewing manager's note (legacy `reviewer_notes`); the claimant sees it on a close only. */
    reviewerNotes: z.string().nullable(),
    /** Pending requests only: what approving does to the claimant's week against the weekly hours limit (advisory; legacy `ShiftSwapImpact::forClaim`). */
    impact: ShiftSwapPartyImpact.nullable(),
    /** Pending requests only: the shift has started, so Close is all that is left (legacy `shift_started`); null otherwise. */
    shiftStarted: z.boolean().nullable(),
  })
  .meta({ id: 'ShiftClaimView' });

/** Employees › Shift Claims (legacy `GET /v1/shift-claim-requests`): the location's claim requests, newest first, optionally one status. */
export const ShiftClaims = defineQuery({
  name: 'labor.shiftClaims',
  summary: "A location's open-shift claim requests, newest first, optionally of one status.",
  input: z.object({ locationId: LocationId, status: ShiftClaimStatus.optional() }),
  output: z.object({ claims: z.array(ShiftClaimView) }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/shift-claim-requests'],
});

/**
 * Open Shift Requests › Approve / Deny / Close in the Back Office (legacy `PUT /v1/shift-claim-requests/{id}`): a
 * manager decides a pending request; approving gives the requester the shift and fills the other requests, closing takes
 * it out of the queue with no ruling. `notes` is the manager's note, kept with the outcome (legacy `reviewer_notes`).
 */
export const ReviewShiftClaimAsManager = defineCommand({
  ...scheduleAdmin,
  name: 'labor.reviewShiftClaimAsManager',
  summary: "Approve, deny or close an employee's pending request for an open shift from the Back Office, with an optional note.",
  input: z.object({
    shiftId: ShiftId, locationId: LocationId, claimId: z.uuid(), decision: z.enum(['approved', 'denied', 'closed']),
    notes: z.string().trim().max(1000).nullable().optional(),
  }),
  output: ClaimOutput,
  errors: [ShiftNotFound, ShiftClaimRefused, CloudAuthoritative],
  legacy: ['PUT /v1/shift-claim-requests/{id}'],
});

/** Open Shift Requests › Remove (legacy `DELETE /v1/shift-claim-requests/{id}`): a manager takes a request off the shift, whatever its status. */
export const RemoveShiftClaim = defineCommand({
  ...scheduleAdmin,
  name: 'labor.removeShiftClaim',
  summary: "Remove an employee's request for an open shift from the Back Office.",
  input: z.object({ shiftId: ShiftId, locationId: LocationId, claimId: z.uuid() }),
  output: z.object({ shift: ShiftView }),
  errors: [ShiftNotFound, ShiftClaimRefused, CloudAuthoritative],
  legacy: ['DELETE /v1/shift-claim-requests/{id}'],
});

// ---- Employees › Shift Swap (SCR-081; legacy ShiftSwapRequests.vue, ShiftSwapReviewDialog.vue, ShiftSwapController) ---
// A request rides on the requester's shift (config field `swaps`), as claims do. Core `labor/shift-swaps` holds the rules.

/** Every status legacy stores; Back Office review writes approved and denied, and carrying one out supersedes the others. */
export const ShiftSwapStatus = z.enum(['pending', 'accepted', 'approved', 'denied', 'declined', 'cancelled', 'superseded', 'opened', 'closed']).meta({ id: 'ShiftSwapStatus' });

/** Core `SwapRefusal`, by legacy's message; `conflict` names the overlapping shift and `date` the day off. */
export const ShiftSwapRefused = defineError({
  code: 'labor.shift_swap_refused', httpStatus: 422, message: 'errors.labor.shift_swap_refused', retryable: false,
  details: z.object({
    rule: z.enum([
      'claims_disabled', 'open_shift', 'unpublished', 'started', 'already_requested', 'own_shift', 'not_on_roster', 'party_off_roster', 'not_live', 'not_accepted',
      'swap_fixed', 'no_recipient', 'shift_gone', 'offered_gone', 'not_requesters', 'offered_not_recipients', 'offered_started', 'overlaps', 'time_off',
    ]),
    conflict: z.object({ businessDate: LocalDate, startTime: z.string(), endTime: z.string() }).optional(),
    date: LocalDate.optional(),
  }),
});

const SwapShiftView = z.object({ shiftId: ShiftId, businessDate: LocalDate, startTime: WallTime, endTime: WallTime, jobRoleName: z.string().nullable() });

export const ShiftSwapImpact = z
  .object({ receiver: ShiftSwapPartyImpact.nullable(), requester: ShiftSwapPartyImpact.nullable() })
  .meta({ id: 'ShiftSwapImpact' });

export const ShiftSwapView = z
  .object({
    swapId: z.uuid(),
    kind: z.enum(['release', 'swap']),
    requesterId: EmployeeId,
    requesterName: z.string().nullable(),
    recipientId: EmployeeId.nullable(),
    recipientName: z.string().nullable(),
    shift: SwapShiftView,
    /** The shift the recipient gives back on a swap (B951: legacy never showed it); null on a release or once deleted. */
    offeredShift: SwapShiftView.nullable(),
    status: ShiftSwapStatus,
    reason: z.string().nullable(),
    requestedAt: LocationTime,
    reviewedBy: z.object({ kind: z.enum(['staff', 'user']), id: z.string(), name: z.string().nullable() }).nullable(),
    reviewedAt: LocationTime.nullable(),
    reviewerNotes: z.string().nullable(),
    /** What approving does to each party's week, on live requests only (B951). */
    impact: ShiftSwapImpact.nullable(),
    /** Live requests only: the shift, or the one offered back, has started, so Close is all that is left (legacy `shift_started`, B1601); null otherwise. */
    shiftStarted: z.boolean().nullable(),
  })
  .meta({ id: 'ShiftSwapView' });

/** A shift Create Swap may release: assigned, published, not started, with no live request on it (B952, B957). */
export const ReleasableShift = z
  .object({ shiftId: ShiftId, employeeId: EmployeeId, employeeName: z.string().nullable(), businessDate: LocalDate, startTime: WallTime, endTime: WallTime })
  .meta({ id: 'ReleasableShift' });

/** Employees › Shift Swap (legacy `GET /v1/shift-swap-requests`): the location's requests, newest first, and Create Swap's shifts. */
export const ShiftSwaps = defineQuery({
  name: 'labor.shiftSwaps',
  summary: "A location's shift swap requests, newest first, optionally of one status, with the shifts a manager may release.",
  input: z.object({ locationId: LocationId, status: ShiftSwapStatus.optional() }),
  output: z.object({ swaps: z.array(ShiftSwapView), releasable: z.array(ReleasableShift) }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/shift-swap-requests', 'GET /v1/shifts'],
});

/**
 * Create Swap (legacy `POST /v1/shift-swap-requests`): a manager releases a shift for its employee, optionally naming
 * who takes it. The request is pending until reviewed.
 */
export const ReleaseShift = defineCommand({
  ...scheduleAdmin,
  name: 'labor.releaseShift',
  summary: "Release a shift for its employee (Create Swap), optionally naming who takes it; a manager reviews it.",
  input: z.object({
    shiftId: ShiftId, locationId: LocationId, swapId: z.uuid(), recipientId: EmployeeId.nullable().optional(), reason: z.string().trim().max(1000).nullable().optional(),
  }),
  output: z.object({ swapId: z.uuid(), shift: ShiftView }),
  errors: [ShiftNotFound, ShiftSwapRefused, CloudAuthoritative],
  legacy: ['POST /v1/shift-swap-requests'],
});

/**
 * Review Shift Swap › Deny / Approve Swap (legacy `PUT /v1/shift-swap-requests/{id}`): the signed-in manager rules on a
 * live request, with optional notes. Approving moves the shift to the recipient (on a release, whoever the manager
 * picks), and on a swap moves the offered shift back to the requester as a companion command. `overtime` names the
 * parties the approval takes over the weekly limit (legacy's activity-log record).
 */
export const ReviewShiftSwap = defineCommand({
  ...scheduleAdmin,
  name: 'labor.reviewShiftSwap',
  summary: 'Approve, deny or close a shift swap request from the Back Office; approving moves the shift, or both shifts.',
  input: z.object({
    // closed: out of the queue with no ruling, the note shown to the employee (legacy b55025272; B1601).
    shiftId: ShiftId, locationId: LocationId, swapId: z.uuid(), decision: z.enum(['approved', 'denied', 'closed']),
    recipientId: EmployeeId.nullable().optional(), notes: z.string().trim().max(1000).nullable().optional(),
  }),
  output: z.object({ outcome: z.enum(['approved', 'denied', 'closed']), shift: ShiftView, overtime: ShiftSwapImpact.nullable() }),
  errors: [ShiftNotFound, ShiftSwapRefused, CloudAuthoritative],
  legacy: ['PUT /v1/shift-swap-requests/{id}'],
});

/**
 * The offered side of legacy `LiveSwapRequests::supersede`: closes, as superseded for good, the live requests on one
 * shift that offer back any of the shifts named. The schedule's own commands send it as a companion when a shift changes
 * hands, moves or is deleted, and an approval when it moves both shifts (coordinator's review of #1954, F3 and F4).
 */
export const SupersedeShiftSwaps = defineCommand({
  ...scheduleAdmin,
  name: 'labor.supersedeShiftSwaps',
  summary: 'Close as superseded the live swap requests on a shift that offer back shifts which changed hands, moved or were deleted.',
  input: z.object({ shiftId: ShiftId, locationId: LocationId, offeredShiftIds: z.array(ShiftId).min(1).max(2) }),
  output: z.object({ superseded: z.array(z.uuid()) }),
  errors: [ShiftNotFound, CloudAuthoritative],
  legacy: [],
  // A direct call would close an untouched live swap (B977): it runs only as Edit Shift's, Delete Shift's and an approval's companion.
  companionOnly: true,
});

/**
 * One shift as a terminated employee leaves it, as the system leaves it (legacy API 399779a7f
 * `OpenTerminatedEmployeeShiftsAction`): the shift opens while it is still theirs and has not started, their pending
 * claims on it are filled, and (at the cloud only, which owns swaps) its live requests are superseded once it opens, a
 * live swap addressed to them is superseded and a live release offered to them goes back to pending with nobody named.
 * A location node writes no swaps. Refused while the employee is still on the active staff, and when nothing on the
 * shift is theirs to settle. Planned by `labor.upsertStaffMember` when it deactivates them.
 * `only: 'swaps'` writes the swaps alone: the cloud's follow-on to a deactivation a node recorded, which already opened
 * the shifts and filled the claims (B1602). It is the cloud's alone: a node answers it 403 `config.cloud_authoritative`,
 * as swaps are cloud-authoritative.
 */
export const SettleLeaverRequests = defineCommand({
  ...scheduleAdmin,
  name: 'labor.settleLeaverRequests',
  summary: "Settle a shift for a terminated employee: opened if theirs and unstarted, their claims filled, swaps to them closed (the swaps at the cloud only).",
  input: z.object({ shiftId: ShiftId, locationId: LocationId, employeeId: EmployeeId, only: z.enum(['swaps']).optional() }),
  output: z.object({ settled: z.boolean() }),
  errors: [ShiftNotFound, CloudAuthoritative],
  legacy: [],
});

const Planned = z.object({ minutes: z.int().nonnegative(), cost: Money.nullable() });

/**
 * Employees › Schedule (week view): the week's schedule (null when none), its shifts, and the planned hours and labor cost
 * per employee, per day and in total (core `plannedLaborCost`: the role's rate, else the employee's; a null cost is a
 * dash). `scheduledWeeks` are the week starts within 6 weeks either side that hold a schedule (Copy Week's choices).
 */
export const ScheduleWeek = defineQuery({
  name: 'labor.scheduleWeek',
  summary: "A location's schedule for one week: shifts, planned hours and labor cost per employee and per day.",
  input: z.object({ locationId: LocationId, weekStartsOn: LocalDate }),
  output: z.object({
    weekStartsOn: LocalDate,
    schedule: ScheduleView.nullable(),
    shifts: z.array(ShiftView),
    employees: z.array(Planned.extend({ employeeId: EmployeeId })),
    days: z.array(Planned.extend({ businessDate: LocalDate })),
    total: Planned,
    scheduledWeeks: z.array(LocalDate),
  }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/schedules', 'GET /v1/schedules/{id}', 'GET /v1/shifts', 'GET /v1/schedules/{id}/labor-summary'],
});

/**
 * The register's My Schedule › My Shifts: the employee's published shifts from today to 14 days out, with core's
 * `myShiftsSummary` (two halves, gross hours, days off, the shift up next and whether it is on now) and who works each
 * shift's day with them.
 */
export const MySchedule = defineQuery({
  name: 'labor.mySchedule',
  summary: "An employee's published shifts for the next 14 days, with the shift up next and the week totals.",
  /** `at`: the register's clock, which sets today and the shift on now. */
  input: z.object({ locationId: LocationId, employeeId: EmployeeId, at: EpochMs }),
  output: z.object({
    today: LocalDate,
    halves: z.array(z.object({ from: LocalDate, to: LocalDate })),
    shifts: z.array(ShiftView.extend({ half: z.union([z.literal(0), z.literal(1)]), coworkerIds: z.array(EmployeeId) })),
    shiftCount: z.int().nonnegative(),
    scheduledMinutes: z.int().nonnegative(),
    daysOff: z.int().nonnegative(),
    next: z.object({ shiftId: ShiftId, onNow: z.boolean() }).nullable(),
  }),
  permission: { session: 'staff' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/employee-schedules'],
});

// ---- templates ----------------------------------------------------------------------------------------------------
// Schedule Templates (SCR-078, DLG-056, DLG-057; legacy `schedule_templates`, CreateTemplateAction, ApplyTemplateAction):
// a week's shifts kept by weekday with no employee, as location config (`labor.schedule_template`). Applying one is a
// schedule (when the week has none) then one open shift per template shift, written by the client one command each,
// its planned breaks re-planned from the live break definitions (a retired one is skipped and counted).

/** A template's shift: the weekday from the week's first day (0 to 6), the role, times, planned breaks, notes and colour. */
export const TemplateShift = z
  .object({
    dayOffset: z.int().min(0).max(6),
    jobRoleId: JobRoleId.nullable(),
    startTime: WallTime,
    endTime: WallTime,
    breaks: z.array(PlannedBreak),
    notes: z.string().max(1000).nullable(),
    color: z.string().nullable(),
  })
  .meta({ id: 'TemplateShift' });

export const ScheduleTemplateView = z
  .object({
    templateId: ScheduleTemplateId,
    locationId: LocationId,
    name: z.string(),
    /** Legacy's "Created from week of 2026-10-12" (the Source column reads "Week of Oct 12, 2026"). */
    description: z.string().nullable(),
    shifts: z.array(TemplateShift),
    /** When it was saved, on the location clock. */
    createdAt: LocationTime,
    version: z.int().nonnegative(),
  })
  .meta({ id: 'ScheduleTemplateView' });

export const ScheduleTemplateNotFound = defineError({
  code: 'labor.schedule_template_not_found', httpStatus: 404, message: 'errors.labor.schedule_template_not_found', retryable: false,
  details: z.object({ templateId: ScheduleTemplateId }),
});

/** Save as Template (legacy `POST /v1/schedule-templates`): the week's shifts, by weekday, without their employees. */
export const SaveScheduleTemplate = defineCommand({
  ...scheduleAdmin,
  name: 'labor.saveScheduleTemplate',
  summary: "Save a week's shifts as a reusable template (by weekday, without employees).",
  input: z.object({ templateId: ScheduleTemplateId, locationId: LocationId, name: z.string().trim().min(1).max(255), scheduleId: ScheduleId }),
  output: ScheduleTemplateView,
  errors: [ScheduleNotFound, CloudAuthoritative],
  legacy: ['POST /v1/schedule-templates'],
});

/** Schedule Templates › Delete Template (legacy `DELETE /v1/schedule-templates/{id}`). */
export const DeleteScheduleTemplate = defineCommand({
  ...scheduleAdmin,
  name: 'labor.deleteScheduleTemplate',
  summary: 'Delete a schedule template.',
  input: z.object({ templateId: ScheduleTemplateId, locationId: LocationId }),
  output: ScheduleTemplateView,
  errors: [ScheduleTemplateNotFound, CloudAuthoritative],
  legacy: ['DELETE /v1/schedule-templates/{id}'],
});

/** Employees › Schedule Templates: the location's templates, newest first. */
export const ScheduleTemplates = defineQuery({
  name: 'labor.scheduleTemplates',
  summary: "A location's schedule templates with their shifts.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ templates: z.array(ScheduleTemplateView) }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/schedule-templates', 'POST /v1/schedules/{id}/apply-template'],
});

export const scheduleCommands = [CreateSchedule, PublishSchedule, DeleteSchedule, UpsertShift, DeleteShift, ResolveShiftConflict, ClaimOpenShift, ReviewShiftClaim, ReviewShiftClaimAsManager, RemoveShiftClaim, ReleaseShift, ReviewShiftSwap, SupersedeShiftSwaps, SettleLeaverRequests, SaveScheduleTemplate, DeleteScheduleTemplate] as const;
export const scheduleQueries = [ScheduleWeek, MySchedule, ScheduleTemplates, ShiftClaims, ShiftSwaps] as const;
